import {ForecastDaysMode} from '@/app/constants/forecast';
import {RouteName} from '@/app/constants/routing';
import {RouteKeywordsMap} from '@/app/interfaces/routing';

type RouteUrlParamNames = 'lang' | 'location' | 'days' | 'selectedDate' | 'selectedWeekday';

interface MainPageParamsSchema {
	name: RouteName.mainPage;
	params: {}; // eslint-disable-line @typescript-eslint/ban-types
}
interface WeatherParamsSchema {
	name: RouteName.weather;
	params: {
		location: {id: string};
		daysMode?: ForecastDaysMode;

		/**
		 * Selected date is ISO date format;
		 * Will be transformed to weekday format when daysMode is "ForecastDaysMode.Days7"
		 */
		dayDate?: string;

		/**
		 * Alternative to dayDate when need to set directly weekday;
		 * Value of "daysMode" in this case is ignored
		 */
		dayWeekday?: number;
	};
}

interface MapParamsSchema {
	name: RouteName.map;
	params: {
		location: string;
	};
}

export type AnyParamsSchema = MainPageParamsSchema | WeatherParamsSchema | MapParamsSchema;

export function urlParamsBuilder<N extends AnyParamsSchema['name']>(
	routeKeywords: RouteKeywordsMap,
	routeName: N,
	params: Extract<AnyParamsSchema, {name: N}>['params'],
	language: string | undefined,
) {
	// Prepare keyword maps
	const weekdaysKeywordMap = [
		routeKeywords.sunday,
		routeKeywords.monday,
		routeKeywords.tuesday,
		routeKeywords.wednesday,
		routeKeywords.thursday,
		routeKeywords.friday,
		routeKeywords.saturday,
	];

	const forecastDaysModeToUrlKeywordMap = {
		[ForecastDaysMode.Days7]: undefined,
		[ForecastDaysMode.Days10]: routeKeywords['10days'],
	};

	// Start params map build
	const totalParams: Partial<Record<RouteUrlParamNames, string>> = {};

	// Define a route language
	if (language) {
		totalParams.lang = language;
	}

	// Build main page url
	if (routeName === RouteName.mainPage) {
		// No specific parameters
	}

	// Build weather page url
	if (routeName === RouteName.weather) {
		const {location, daysMode, dayDate, dayWeekday} = params as WeatherParamsSchema['params'];

		// Set location
		if (location) {
			totalParams.location = location.id;
		}

		// Determinate selected date based on the "dayDate" value
		if (dayDate) {
			if (daysMode === ForecastDaysMode.Days7) {
				let date = new Date(dayDate);
				date = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
				const weekday = date.getDay();
				totalParams.selectedWeekday = weekdaysKeywordMap[weekday];
			} else {
				totalParams.selectedDate = dayDate;
			}
		}

		// Determinate selected date based on the "dayWeekday" value as an alternative to "dayDate"
		if (dayWeekday !== undefined) {
			totalParams.selectedWeekday = weekdaysKeywordMap[dayWeekday];
		}

		// Determinate days view mode
		if (!totalParams.selectedDate && !totalParams.selectedWeekday && daysMode) {
			const days = forecastDaysModeToUrlKeywordMap[daysMode];
			if (days) {
				totalParams.days = days;
			}
		}
	}

	// Build map page url
	if (routeName === RouteName.map) {
		const {location} = params as MapParamsSchema['params'];
		totalParams.location = location;
	}

	return totalParams;
}
